import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import extractDomain from 'extract-domain';
import { extension, ExtensionPriority, keyBinding, NamedShortcut, command, MarkExtension, ExtensionTag, isElementDomNode, includes, omitExtraAttributes, getMarkRange, getSelectedWord, isTextSelection, isSelectionEmpty, isAllSelection, isMarkActive, updateMark, removeMark, getMatchString, composeTransactionSteps, getChangedRanges, within, findMatches, last } from '@remirror/core';
import { undoDepth } from '@remirror/pm/history';
import { TextSelection } from '@remirror/pm/state';
import { ReplaceAroundStep, ReplaceStep } from '@remirror/pm/transform';

/**
 * The top 50 Top Level Domains (as of May 2022), ordered by most popular, for performance.
 */
var TOP_50_TLDS = ['com', 'de', 'net', 'org', 'uk', 'cn', 'ga', 'nl', 'cf', 'ml', 'tk', 'ru', 'br', 'gq', 'xyz', 'fr', 'eu', 'info', 'co', 'au', 'ca', 'it', 'in', 'ch', 'pl', 'es', 'online', 'us', 'top', 'be', 'jp', 'biz', 'se', 'at', 'dk', 'cz', 'za', 'me', 'ir', 'icu', 'shop', 'kr', 'site', 'mx', 'hu', 'io', 'cc', 'club', 'no', 'cyou'];

var _excluded = ["auto", "target"];

var _dec, _dec2, _dec3, _dec4, _dec5, _class, _class2;
var UPDATE_LINK = 'updateLink'; // Based on https://gist.github.com/dperini/729294

var DEFAULT_AUTO_LINK_REGEX = /(?:(?:(?:https?|ftp):)?\/\/)?(?:\S+(?::\S*)?@)?(?:(?:[\da-z\u00A1-\uFFFF][\w\u00A1-\uFFFF-]{0,62})?[\da-z\u00A1-\uFFFF]\.)*(?:(?:\d(?!\.)|[a-z\u00A1-\uFFFF])(?:[\da-z\u00A1-\uFFFF][\w\u00A1-\uFFFF-]{0,62})?[\da-z\u00A1-\uFFFF]\.)+[a-z\u00A1-\uFFFF]{2,}(?::\d{2,5})?(?:[#/?]\S*)?/gi;
/**
 * Can be an empty string which sets url's to '//google.com'.
 */

var LinkExtension = (_dec = extension({
  defaultOptions: {
    autoLink: false,
    defaultProtocol: '',
    selectTextOnClick: false,
    openLinkOnClick: false,
    autoLinkRegex: DEFAULT_AUTO_LINK_REGEX,
    autoLinkAllowedTLDs: TOP_50_TLDS,
    defaultTarget: null,
    supportedTargets: [],
    extractHref
  },
  staticKeys: ['autoLinkRegex'],
  handlerKeyOptions: {
    onClick: {
      earlyReturnValue: true
    }
  },
  handlerKeys: ['onActivateLink', 'onShortcut', 'onUpdateLink', 'onClick'],
  defaultPriority: ExtensionPriority.Medium
}), _dec2 = keyBinding({
  shortcut: NamedShortcut.InsertLink
}), _dec3 = command(), _dec4 = command(), _dec5 = command(), _dec(_class = (_class2 = class LinkExtension extends MarkExtension {
  constructor() {
    super(...arguments);

    _defineProperty(this, "_autoLinkRegexNonGlobal", undefined);
  }

  get name() {
    return 'link';
  }
  /**
   * The autoLinkRegex option with the global flag removed, ensure no "lastIndex" state is maintained over multiple matches
   * @private
   */


  createTags() {
    return [ExtensionTag.Link, ExtensionTag.ExcludeInputRules];
  }

  createMarkSpec(extra, override) {
    var _override$parseDOM;

    var AUTO_ATTRIBUTE = 'data-link-auto';

    var getTargetObject = target => {
      var _this$options = this.options,
          defaultTarget = _this$options.defaultTarget,
          supportedTargets = _this$options.supportedTargets;
      var targets = defaultTarget ? [...supportedTargets, defaultTarget] : supportedTargets;
      return target && includes(targets, target) ? {
        target
      } : undefined;
    };

    return _objectSpread(_objectSpread({
      inclusive: false,
      excludes: '_'
    }, override), {}, {
      attrs: _objectSpread(_objectSpread({}, extra.defaults()), {}, {
        href: {},
        target: {
          default: this.options.defaultTarget
        },
        auto: {
          default: false
        }
      }),
      parseDOM: [{
        tag: 'a[href]',
        getAttrs: node => {
          if (!isElementDomNode(node)) {
            return false;
          }

          var href = node.getAttribute('href');
          var auto = node.hasAttribute(AUTO_ATTRIBUTE);
          return _objectSpread(_objectSpread({}, extra.parse(node)), {}, {
            href,
            auto
          }, getTargetObject(node.getAttribute('target')));
        }
      }, ...((_override$parseDOM = override.parseDOM) !== null && _override$parseDOM !== void 0 ? _override$parseDOM : [])],
      toDOM: node => {
        var _omitExtraAttributes = omitExtraAttributes(node.attrs, extra);
            _omitExtraAttributes.auto;
            _omitExtraAttributes.target;
            var rest = _objectWithoutProperties(_omitExtraAttributes, _excluded);

        var auto = node.attrs.auto ? {
          [AUTO_ATTRIBUTE]: ''
        } : {};
        var rel = 'noopener noreferrer nofollow';

        var attrs = _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, extra.dom(node)), rest), {}, {
          rel
        }, auto), getTargetObject(node.attrs.target));

        return ['a', attrs, 0];
      }
    });
  }

  onCreate() {
    var autoLinkRegex = this.options.autoLinkRegex; // Remove the global flag from autoLinkRegex, and wrap in start (^) and end ($) terminator to test for exact match

    this._autoLinkRegexNonGlobal = new RegExp("^".concat(autoLinkRegex.source, "$"), autoLinkRegex.flags.replace('g', ''));
  }
  /**
   * Add a handler to the `onActivateLink` to capture when .
   */


  shortcut(_ref) {
    var tr = _ref.tr;
    var selectedText = '';
    var _tr$selection = tr.selection,
        from = _tr$selection.from,
        to = _tr$selection.to,
        empty = _tr$selection.empty,
        $from = _tr$selection.$from;
    var expandedSelection = false;
    var mark = getMarkRange($from, this.type); // When the selection is empty, expand it to the active mark

    if (empty) {
      var selectedWord = mark !== null && mark !== void 0 ? mark : getSelectedWord(tr);

      if (!selectedWord) {
        return false;
      }

      selectedText = selectedWord.text;
      from = selectedWord.from;
      to = selectedWord.to;
      expandedSelection = true;
    }

    if (from === to) {
      return false;
    }

    if (!expandedSelection) {
      selectedText = tr.doc.textBetween(from, to);
    }

    this.options.onActivateLink(selectedText);
    this.options.onShortcut({
      activeLink: mark ? {
        attrs: mark.mark.attrs,
        from: mark.from,
        to: mark.to
      } : undefined,
      selectedText,
      from,
      to
    });
    return true;
  }
  /**
   * Create or update the link if it doesn't currently exist at the current
   * selection or provided range.
   */


  updateLink(attrs, range) {
    return props => {
      var tr = props.tr;
      var selectionIsValid = isTextSelection(tr.selection) && !isSelectionEmpty(tr.selection) || isAllSelection(tr.selection) || isMarkActive({
        trState: tr,
        type: this.type
      });

      if (!selectionIsValid && !range) {
        return false;
      }

      tr.setMeta(this.name, {
        command: UPDATE_LINK,
        attrs,
        range
      });
      return updateMark({
        type: this.type,
        attrs,
        range
      })(props);
    };
  }
  /**
   * Select the link at the current location.
   */


  selectLink() {
    return this.store.commands.selectMark.original(this.type);
  }
  /**
   * Remove the link at the current selection
   */


  removeLink(range) {
    return props => {
      var tr = props.tr;

      if (!isMarkActive(_objectSpread({
        trState: tr,
        type: this.type
      }, range))) {
        return false;
      }

      return removeMark({
        type: this.type,
        expand: true,
        range
      })(props);
    };
  }
  /**
   * Create the paste rules that can transform a pasted link in the document.
   */


  createPasteRules() {
    return [{
      type: 'mark',
      regexp: this.options.autoLinkRegex,
      markType: this.type,
      getAttributes: (url, isReplacement) => ({
        href: this.buildHref(getMatchString(url)),
        auto: !isReplacement
      }),
      transformMatch: match => {
        var url = getMatchString(match);

        if (!url) {
          return false;
        }

        var href = this.buildHref(url);

        if (!this.isValidTLD(href)) {
          return false;
        }

        return url;
      }
    }];
  }
  /**
   * Track click events passed through to the editor.
   */


  createEventHandlers() {
    return {
      clickMark: (event, clickState) => {
        var markRange = clickState.getMark(this.type);

        if (!markRange) {
          return;
        }

        var attrs = markRange.mark.attrs;

        var data = _objectSpread(_objectSpread({}, attrs), markRange); // If one of the handlers returns `true` then return early.


        if (this.options.onClick(event, data)) {
          return true;
        }

        var handled = false;

        if (this.options.openLinkOnClick) {
          handled = true;
          var href = attrs.href;
          window.open(href, '_blank');
        }

        if (this.options.selectTextOnClick) {
          handled = true;
          this.store.commands.selectText(markRange);
        }

        return handled;
      }
    };
  }
  /**
   * The plugin for handling click events in the editor.
   *
   * TODO extract this into the events extension and move that extension into
   * core.
   */


  createPlugin() {
    return {
      props: {
        handleClick: (view, pos) => {
          if (!this.options.selectTextOnClick && !this.options.openLinkOnClick) {
            return false;
          }

          var _view$state = view.state,
              doc = _view$state.doc,
              tr = _view$state.tr;
          var range = getMarkRange(doc.resolve(pos), this.type);

          if (!range) {
            return false;
          }

          if (this.options.openLinkOnClick) {
            var href = range.mark.attrs.href;
            window.open(href, '_blank');
          }

          if (this.options.selectTextOnClick) {
            var $start = doc.resolve(range.from);
            var $end = doc.resolve(range.to);
            var transaction = tr.setSelection(new TextSelection($start, $end));
            view.dispatch(transaction);
          }

          return true;
        }
      },
      appendTransaction: (transactions, prevState, state) => {
        var transactionsWithLinkMeta = transactions.filter(tr => !!tr.getMeta(this.name));
        transactionsWithLinkMeta.forEach(tr => {
          var trMeta = tr.getMeta(this.name);

          if (trMeta.command === UPDATE_LINK) {
            var range = trMeta.range,
                attrs = trMeta.attrs;
            var selection = state.selection,
                _doc = state.doc;
            var _meta = {
              range,
              selection,
              doc: _doc,
              attrs
            };

            var _ref2 = range !== null && range !== void 0 ? range : selection,
                from = _ref2.from,
                to = _ref2.to;

            this.options.onUpdateLink(_doc.textBetween(from, to), _meta);
          }
        });

        if (!this.options.autoLink) {
          return;
        }

        var isUndo = undoDepth(prevState) - undoDepth(state) === 1;

        if (isUndo) {
          return; // Don't execute auto link logic if an undo was performed.
        }

        var docChanged = transactions.some(tr => tr.docChanged);

        if (!docChanged) {
          return; // Don't execute auto link logic if nothing has changed.
        } // Create a single transaction, by combining all transactions


        var composedTransaction = composeTransactionSteps(transactions, prevState);
        var changes = getChangedRanges(composedTransaction, [ReplaceAroundStep, ReplaceStep]);
        var mapping = composedTransaction.mapping;
        var tr = state.tr,
            doc = state.doc;

        var _this$store$chain = this.store.chain(tr),
            updateLink = _this$store$chain.updateLink,
            removeLink = _this$store$chain.removeLink;

        changes.forEach(_ref3 => {
          var from = _ref3.from,
              to = _ref3.to,
              prevFrom = _ref3.prevFrom,
              prevTo = _ref3.prevTo;
          // Remove auto links that are no longer valid
          this.getLinkMarksInRange(prevState.doc, prevFrom, prevTo, true).forEach(prevMark => {
            var newFrom = mapping.map(prevMark.from);
            var newTo = mapping.map(prevMark.to);
            var newMarks = this.getLinkMarksInRange(doc, newFrom, newTo, true);
            newMarks.forEach(newMark => {
              var _this$_autoLinkRegexN, _this$_autoLinkRegexN2;

              var wasLink = (_this$_autoLinkRegexN = this._autoLinkRegexNonGlobal) === null || _this$_autoLinkRegexN === void 0 ? void 0 : _this$_autoLinkRegexN.test(prevMark.text);
              var isLink = (_this$_autoLinkRegexN2 = this._autoLinkRegexNonGlobal) === null || _this$_autoLinkRegexN2 === void 0 ? void 0 : _this$_autoLinkRegexN2.test(newMark.text);

              if (wasLink && !isLink) {
                removeLink({
                  from: newMark.from,
                  to: newMark.to
                }).tr();
              }
            });
          }); // Store all the callbacks we need to make

          var onUpdateCallbacks = []; // Find text that can be auto linked

          tr.doc.nodesBetween(from, to, (node, pos) => {
            if (!node.isTextblock || !node.type.allowsMarkType(this.type)) {
              return;
            }

            var nodeText = tr.doc.textBetween(pos, pos + node.nodeSize, undefined, ' ');
            this.findAutoLinks(nodeText) // calculate link position
            .map(link => _objectSpread(_objectSpread({}, link), {}, {
              from: pos + link.start + 1,
              to: pos + link.end + 1
            })).filter(link => {
              // Determine if found link is within the changed range
              return within(link.from, from, to) || within(link.to, from, to) || within(from, link.from, link.to) || within(to, link.from, link.to);
            }).filter(link => {
              // Avoid overwriting manually created links
              var marks = this.getLinkMarksInRange(tr.doc, link.from, link.to, false);
              return marks.length === 0;
            }).forEach(_ref4 => {
              var from = _ref4.from,
                  to = _ref4.to,
                  href = _ref4.href,
                  text = _ref4.text;
              var attrs = {
                href,
                auto: true
              };
              var range = {
                from,
                to
              };
              updateLink(attrs, range).tr();
              onUpdateCallbacks.push({
                attrs,
                range,
                text
              });
            });
          });
          onUpdateCallbacks.forEach(_ref5 => {
            var range = _ref5.range,
                attrs = _ref5.attrs,
                text = _ref5.text;
            var doc = tr.doc,
                selection = tr.selection;
            this.options.onUpdateLink(text, {
              doc,
              selection,
              range,
              attrs
            });
          });
        });

        if (tr.steps.length === 0) {
          return;
        }

        return tr;
      }
    };
  }

  buildHref(url) {
    return this.options.extractHref({
      url,
      defaultProtocol: this.options.defaultProtocol
    });
  }

  getLinkMarksInRange(doc, from, to, isAutoLink) {
    var linkMarks = [];

    if (from === to) {
      var $pos = doc.resolve(from);
      $pos.marks().forEach(() => {
        var range = getMarkRange($pos, this.type);

        if ((range === null || range === void 0 ? void 0 : range.mark.attrs.auto) === isAutoLink) {
          linkMarks.push(range);
        }
      });
    } else {
      doc.nodesBetween(from, to, (node, pos) => {
        var _node$marks;

        var marks = (_node$marks = node.marks) !== null && _node$marks !== void 0 ? _node$marks : [];
        var linkMark = marks.find(_ref6 => {
          var type = _ref6.type,
              attrs = _ref6.attrs;
          return type === this.type && attrs.auto === isAutoLink;
        });

        if (linkMark) {
          linkMarks.push({
            from: pos,
            to: pos + node.nodeSize,
            mark: linkMark,
            text: node.textContent
          });
        }
      });
    }

    return linkMarks;
  }

  findAutoLinks(str) {
    var toAutoLink = [];

    var _iterator = _createForOfIteratorHelper(findMatches(str, this.options.autoLinkRegex)),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var match = _step.value;
        var text = getMatchString(match);

        if (!text) {
          continue;
        }

        var href = this.buildHref(text);

        if (!this.isValidTLD(href)) {
          continue;
        }

        toAutoLink.push({
          text,
          href,
          start: match.index,
          end: match.index + text.length
        });
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    return toAutoLink;
  }

  isValidTLD(str) {
    var autoLinkAllowedTLDs = this.options.autoLinkAllowedTLDs;

    if (autoLinkAllowedTLDs.length === 0) {
      return true;
    }

    var domain = extractDomain(str);

    if (domain === '') {
      // Not a domain
      return true;
    }

    var tld = last(domain.split('.'));
    return autoLinkAllowedTLDs.includes(tld);
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "shortcut", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "shortcut"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "updateLink", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "updateLink"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "selectLink", [_dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "selectLink"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "removeLink", [_dec5], Object.getOwnPropertyDescriptor(_class2.prototype, "removeLink"), _class2.prototype)), _class2)) || _class);
/**
 * Extract the `href` from the provided text.
 *
 * @remarks
 *
 * This will return the `url` text with a `${defaultProtocol}//` or `mailto:` prefix if needed.
 */

function extractHref(_ref7) {
  var url = _ref7.url,
      defaultProtocol = _ref7.defaultProtocol;
  var startsWithProtocol = /^((?:https?|ftp)?:)\/\//.test(url); // This isn't 100% precise because we allowed URLs without protocol
  // For example, userid@example.com could be email address or link http://userid@example.com

  var isEmail = !startsWithProtocol && url.includes('@');

  if (isEmail) {
    return "mailto:".concat(url);
  }

  return startsWithProtocol ? url : "".concat(defaultProtocol, "//").concat(url);
}

export { LinkExtension, TOP_50_TLDS, extractHref };
